import './styles.css';
import { reloadLatestApp } from './refresh-app';
import { createCoverageApi, CoverageApiError } from './api/client';
import { startAdminApp } from './admin/admin-app';
import { startAnalyticsApp } from './analytics/analytics-app';
import type {
  AssignmentSectionSummary,
  AssignmentSummary,
  IdentityCandidateResponse,
  IdentitySessionResponse,
  IdentityVerificationStartResponse,
  AdminCoverageDashboard,
  AdminCoverageSection,
  CoverageAnalyticsScope,
  GroupMemberSummary,
  HouseConditionStatus,
  LeaderDashboardResponse,
  LeaderAvailableSection,
  PersonSearchResult,
  StoredIdentitySession,
  TeamMember,
  VolunteerBootstrap,
  VolunteerAvailableSection
} from './domain/types';
import { mountCoverageMap } from './map/assignment-map';
import { coverageMetricsForScope, mountAdminCoverageMap, mountSectionPickerMap, type AdminCoverageMapHandle, type SectionPickerMapHandle } from './admin/coverage-map';
import { coverageMetricsTemplate } from './admin/templates';
import {
  assignmentDashboardTemplate,
  assignmentPickerTemplate,
  congratulationsTemplate,
  formTemplate,
  identityCandidatesTemplate,
  identityPinTemplate,
  identitySelfRegistrationTemplate,
  identityVerificationTemplate,
  leaderDashboardTemplate,
  loadingTemplate,
  pickupStacksTemplate,
  shellTemplate,
  teamModalTemplate,
  type AssignmentViewMode,
  type FormState
} from './ui/templates';

const IDENTITY_STORAGE_KEY = 'buckeyeCoverageIdentitySession';
const CLAIM_TOKEN_STORAGE_KEY = 'buckeyeCoverage2ActiveClaimToken';
const PICKUP_CONFIRMED_PREFIX = 'buckeyeCoveragePickupConfirmed:';

const rootElement = document.querySelector<HTMLDivElement>('#app');
if (!rootElement) throw new Error('App root was not found.');
const root: HTMLDivElement = rootElement;
// This delegated control also works after any portal re-renders its menu.
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-refresh-app]') : null;
  if (!target) return;
  event.preventDefault();
  reloadLatestApp();
});
const api = createCoverageApi();
let identitySession = loadIdentitySession();
const routePath = window.location.pathname;
const routeQuery = new URLSearchParams(window.location.search);
const isAnalyticsRoute = routePath.startsWith('/analytics');
const isAdminRoute = routePath.startsWith('/admin') || isAnalyticsRoute;
// Route choice must never depend on an already-existing session. Otherwise a fresh
// visit to /leader is misclassified as Volunteer before the leader can sign in.
// Keep ?view=leader as a backwards-compatible alias for older links.
const isLeaderRoute = routePath.startsWith('/leader') || routeQuery.get('view') === 'leader';
const portal: 'USER' | 'LEADER' | 'ADMIN' = isAdminRoute
  ? 'ADMIN'
  : isLeaderRoute ? 'LEADER' : 'USER';
const adminDestination = isAnalyticsRoute ? '/analytics' : '/admin';
let bootstrap: VolunteerBootstrap | null = null;
let assignment: AssignmentSummary | null = null;
let viewMode: AssignmentViewMode = 'current';
let selectedSectionId = '';
let showCompletedDashboard = false;
let pickupConfirmed = false;
let state: FormState = { neighborhoodId: '', stacks: 2, assignmentChoice: '' };
let volunteerSectionPickerMap: SectionPickerMapHandle | null = null;
let selectedVolunteerSections: AdminCoverageSection[] = [];
let leaderCoverageMap: AdminCoverageMapHandle | null = null;
let leaderSectionPickerMap: SectionPickerMapHandle | null = null;

if (portal === 'ADMIN') {
  if (!identitySession) showIdentityPin();
  else if (!identitySession.person.roles.includes('ADMIN')) showAccessDenied('Admin');
  else if (isAnalyticsRoute) startAnalyticsApp(root, api, identitySession, signOutIdentity);
  else startAdminApp(root, api, identitySession, signOutIdentity);
} else if (portal === 'LEADER') {
  if (identitySession && !identitySession.person.roles.includes('LEADER')) showAccessDenied('Leader');
  else void startLeader();
} else {
  void startUser();
}

async function startUser(): Promise<void> {
  if (!identitySession) {
    showIdentityPin();
    return;
  }
  render(shellTemplate(loadingTemplate(), 'loading'));
  try {
    bootstrap = await api.getVolunteerBootstrap();
    state = {
      ...state,
      neighborhoodId: bootstrap.neighborhoods.some((item) => item.id === state.neighborhoodId && item.available) ? state.neighborhoodId : '',
      stacks: validStacks(state.stacks, bootstrap.maximumStacks) ? state.stacks : bootstrap.defaultStacks
    };

    const savedToken = loadClaimToken();
    if (savedToken) {
      try {
        const savedAssignment = await api.getAssignmentSession(savedToken);
        if (savedAssignment.checkedOutBy.id === identitySession.person.id && savedAssignment.status !== 'COMPLETE') {
          openAssignment(savedAssignment, true);
          return;
        }
        clearClaimToken();
      } catch {
        clearClaimToken();
      }
    }

    const activeAssignments = await api.getMyAssignments(identitySession.sessionToken);
    if (activeAssignments.length === 1) {
      openAssignment(activeAssignments[0]!, true);
      return;
    }
    if (activeAssignments.length > 1) {
      showAssignmentPicker(activeAssignments);
      return;
    }
    showForm();
  } catch (error) {
    if (error instanceof CoverageApiError && error.statusCode === 401) {
      clearIdentitySession();
      clearClaimToken();
      showIdentityPin(error.message);
      return;
    }
    showOpeningError(error, () => void startUser());
  }
}

async function startLeader(): Promise<void> {
  if (!identitySession) {
    showIdentityPin();
    return;
  }
  leaderCoverageMap?.destroy();
  leaderCoverageMap = null;
  leaderSectionPickerMap?.destroy();
  leaderSectionPickerMap = null;
  render(shellTemplate(loadingTemplate('Loading your groups and assignments…'), 'loading'));
  try {
    const [dashboard, leaderBootstrap, coverage] = await Promise.all([
      api.getLeaderDashboard(identitySession.sessionToken),
      api.getVolunteerBootstrap(),
      api.getLeaderCoverage(identitySession.sessionToken)
    ]);
    bootstrap = leaderBootstrap;
    render(shellTemplate(leaderDashboardTemplate(dashboard, leaderBootstrap, coverage), 'live'));
    wireSignOut();
    wireLeaderAssignment(dashboard, coverage);
    wireLeaderReturns();
    const ledGroupIds = new Set(dashboard.groups.map((group) => group.id));
    leaderCoverageMap = mountAdminCoverageMap('leader-master-coverage-map', coverage, {
      canReturnSection: (section) => section.status === 'ASSIGNED' && Boolean(section.assignmentId) &&
        Boolean(section.groupId && ledGroupIds.has(section.groupId)),
      onReturnSection: async (section) => {
        if (!section.assignmentId || !identitySession) return;
        if (!window.confirm(`Take back ${section.neighborhoodName} section ${section.sectionNumber} from ${section.assignedTo?.displayName ?? 'this assignment'}?`)) return;
        await api.releaseLeaderSections(identitySession.sessionToken, section.assignmentId, [section.id]);
        await startLeader();
      }
    });
    wireLeaderCoverageAnalytics(dashboard, coverage);
  } catch (error) {
    if (error instanceof CoverageApiError && error.statusCode === 401) {
      clearIdentitySession();
      clearClaimToken();
      showIdentityPin(error.message);
      return;
    }
    if (error instanceof CoverageApiError && error.statusCode === 403) {
      showAccessDenied('Leader', error.message);
      return;
    }
    showOpeningError(error, () => void startLeader());
  }
}

function wireLeaderAssignment(dashboard: LeaderDashboardResponse, coverage: AdminCoverageDashboard): void {
  if (!identitySession || !bootstrap || !dashboard.groups.length) return;
  const groupSelect = document.querySelector<HTMLSelectElement>('#leader-group');
  const searchInput = document.querySelector<HTMLInputElement>('#leader-person-search');
  const selectedBox = document.querySelector<HTMLDivElement>('#leader-selected-person');
  const form = document.querySelector<HTMLFormElement>('#leader-assignment-form');
  const summary = document.querySelector<HTMLDivElement>('#leader-section-picker-summary');
  const assignButton = document.querySelector<HTMLButtonElement>('#leader-assign-button');
  const mapElement = document.querySelector<HTMLDivElement>('#leader-assignment-section-map');
  let members: GroupMemberSummary[] = [];
  let selectedPerson: GroupMemberSummary | null = null;
  let selectedSections: AdminCoverageSection[] = [];
  const knownGroupIds = new Set(dashboard.groups.map((group) => group.id));
  const defaultGroupId = dashboard.groups[0]?.id ?? '';
  const selectedGroupId = (): string => {
    const current = groupSelect?.value?.trim() ?? '';
    if (current && knownGroupIds.has(current)) return current;
    if (groupSelect && defaultGroupId) groupSelect.value = defaultGroupId;
    return defaultGroupId;
  };
  if (groupSelect && !knownGroupIds.has(groupSelect.value) && defaultGroupId) groupSelect.value = defaultGroupId;

  const setMessage = (text: string, error = false): void => {
    const message = document.querySelector<HTMLDivElement>('#leader-assignment-message');
    if (!message) return;
    message.textContent = text;
    message.classList.toggle('hidden', !text);
    message.classList.toggle('error', error);
  };
  const updateAssignButton = (): void => {
    if (assignButton) assignButton.disabled = !selectedPerson || selectedSections.length === 0;
  };
  const updateSummary = (): void => {
    if (summary) {
      const doors = selectedSections.reduce((total, section) => total + section.doors, 0);
      const stackCount = doors > 0 ? Math.ceil(doors / 50) : 0;
      const neighborhoodCount = new Set(selectedSections.map((section) => section.neighborhoodId)).size;
      summary.innerHTML = `<strong>${selectedSections.length.toLocaleString()} section${selectedSections.length === 1 ? '' : 's'} selected</strong><span>${doors.toLocaleString()} doors · ${stackCount.toLocaleString()} tract stack${stackCount === 1 ? '' : 's'}${neighborhoodCount > 1 ? ` · ${neighborhoodCount} neighborhoods` : ''}</span>`;
    }
    updateAssignButton();
  };
  const showSelected = (): void => {
    if (!selectedBox) return;
    if (!selectedPerson) {
      selectedBox.textContent = '';
      selectedBox.classList.add('hidden');
      updateAssignButton();
      return;
    }
    selectedBox.innerHTML = `<span>Assigning to</span><strong>${escapeHtml(selectedPerson.displayName)}</strong><button id="clear-leader-person" type="button" class="text-button">Change</button>`;
    selectedBox.classList.remove('hidden');
    document.querySelector<HTMLButtonElement>('#clear-leader-person')?.addEventListener('click', () => {
      selectedPerson = null;
      if (searchInput) searchInput.value = '';
      showSelected();
      renderLeaderMemberResults([], '');
      searchInput?.focus();
    });
    updateAssignButton();
  };
  const chooseMember = (personId: string): void => {
    selectedPerson = members.find((member) => member.id === personId) ?? null;
    showSelected();
    renderLeaderMemberResults([], '');
    setMessage('');
  };
  const renderMatches = (): void => {
    const query = searchInput?.value.trim() ?? '';
    const visible = query
      ? members.filter((member) => normalizeText(member.displayName).includes(normalizeText(query)))
      : members.slice(0, 12);
    renderLeaderMemberResults(visible, query);
    document.querySelectorAll<HTMLButtonElement>('[data-leader-person-id]').forEach((button) => {
      button.addEventListener('click', () => chooseMember(button.dataset.leaderPersonId ?? ''));
    });
  };
  const loadMembers = async (): Promise<void> => {
    const groupId = selectedGroupId();
    selectedPerson = null;
    if (searchInput) searchInput.value = '';
    showSelected();
    renderLeaderMemberResults([], '');
    try {
      members = await api.getLeaderGroupMembers(identitySession!.sessionToken, groupId);
      if (groupSelect?.value !== groupId) return;
      renderMatches();
    } catch (error) {
      members = [];
      const results = document.querySelector<HTMLDivElement>('#leader-person-results');
      if (results) results.innerHTML = `<div class="person-search-state error">${escapeHtml(error instanceof Error ? error.message : 'Could not load group members.')}</div>`;
    }
  };
  const toPickerSection = (section: LeaderAvailableSection): AdminCoverageSection => {
    const neighborhoodName = bootstrap!.neighborhoods.find((item) => item.id === section.neighborhoodId)?.name ?? section.neighborhoodId;
    return {
      id: section.id,
      sectionNumber: section.sectionNumber,
      neighborhoodId: section.neighborhoodId,
      neighborhoodName,
      doors: section.doors,
      streetNames: [...section.streetNames],
      status: 'UNASSIGNED',
      routeStatus: 'AVAILABLE',
      ...(section.geometry ? { geometry: section.geometry } : {}),
      ...(section.center ? { center: section.center } : {}),
      ...(section.reservedGroupId ? { groupId: section.reservedGroupId } : {}),
      ...(section.reservedGroupName ? { groupName: section.reservedGroupName } : {}),
      reservedForGroup: true,
      teamMembers: []
    };
  };
  const loadGroupSections = async (): Promise<void> => {
    const groupId = selectedGroupId();
    selectedSections = [];
    leaderSectionPickerMap?.destroy();
    leaderSectionPickerMap = null;
    updateSummary();
    if (mapElement) mapElement.innerHTML = '<div class="map-empty-state"><strong>Loading group sections…</strong><p>Only sections reserved by an admin for this group will appear.</p></div>';
    try {
      const available = await api.getLeaderReservedSections(identitySession!.sessionToken, groupId);
      if (groupSelect?.value !== groupId) return;
      const sections = available.map(toPickerSection);
      const groupName = dashboard.groups.find((group) => group.id === groupId)?.name ?? 'Selected group';
      const groupDashboard: AdminCoverageDashboard = {
        scope: 'MASTER',
        sections,
        metrics: coverageMetricsForScope(sections, { type: 'OVERALL', id: 'OVERALL', label: groupName }),
        updatedAt: coverage.updatedAt
      };
      leaderSectionPickerMap = mountSectionPickerMap(
        'leader-assignment-section-map',
        groupDashboard,
        (next) => {
          selectedSections = next;
          updateSummary();
        },
        100
      );
      if (!leaderSectionPickerMap && mapElement) {
        mapElement.innerHTML = '<div class="map-empty-state"><strong>No group sections available</strong><p>An admin must reserve published sections for this small group before a leader can assign them.</p></div>';
      }
      setMessage('');
    } catch (error) {
      if (mapElement) mapElement.innerHTML = '<div class="map-empty-state"><strong>Could not load group sections</strong><p>Change groups or refresh the page to retry.</p></div>';
      setMessage(error instanceof Error ? error.message : 'Could not load reserved group sections.', true);
    }
  };

  groupSelect?.addEventListener('change', () => {
    void loadMembers();
    void loadGroupSections();
  });
  searchInput?.addEventListener('input', renderMatches);
  searchInput?.addEventListener('focus', renderMatches);
  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    if (!selectedPerson) {
      setMessage('Select a Planning Center group member.', true);
      return;
    }
    const groupId = groupSelect?.value ?? '';
    if (!groupId || !selectedSections.length) {
      setMessage('Choose a group and at least one section on the map.', true);
      return;
    }
    const grouped = Array.from(selectedSections.reduce((map, section) => {
      const list = map.get(section.neighborhoodId) ?? [];
      list.push(section);
      map.set(section.neighborhoodId, list);
      return map;
    }, new Map<string, AdminCoverageSection[]>()).entries());
    const allowAdditional = document.querySelector<HTMLInputElement>('#leader-allow-additional')?.checked === true;
    assignButton?.setAttribute('disabled', 'true');
    if (assignButton) assignButton.textContent = 'Assigning…';
    setMessage('');
    void (async () => {
      const created: AssignmentSummary[] = [];
      for (let index = 0; index < grouped.length; index += 1) {
        const [neighborhoodId, sections] = grouped[index]!;
        created.push(await api.assignLeaderMaps(identitySession!.sessionToken, {
          groupId,
          personId: selectedPerson!.id,
          neighborhoodId,
          requestedStacks: Math.ceil(sections.reduce((total, section) => total + section.doors, 0) / 50),
          selectedSectionIds: sections.map((section) => section.id),
          allowAdditional: allowAdditional || index > 0
        }));
      }
      return created;
    })().then((created) => {
      const sectionCount = created.reduce((total, item) => total + item.sections.length, 0);
      setMessage(`Assigned ${sectionCount} section${sectionCount === 1 ? '' : 's'} to ${selectedPerson!.displayName}.`);
      window.setTimeout(() => void startLeader(), 650);
    }).catch((error) => {
      setMessage(error instanceof Error ? error.message : 'Could not assign maps.', true);
      assignButton?.removeAttribute('disabled');
      if (assignButton) assignButton.textContent = 'Assign selected sections';
      updateAssignButton();
    });
  });
  void loadMembers();
  void loadGroupSections();
  updateSummary();
}

function wireLeaderReturns(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-return-assignment-id]').forEach((button) => {
    button.addEventListener('click', () => {
      if (!identitySession) return;
      const assignmentId = button.dataset.returnAssignmentId ?? '';
      const routeIds = (button.dataset.returnRouteIds ?? '').split(',').filter(Boolean);
      if (!assignmentId || !routeIds.length) return;
      if (!window.confirm('Take back every unfinished section from this assignment? Completed sections will stay in history.')) return;
      button.disabled = true;
      button.textContent = 'Taking back…';
      void api.releaseLeaderSections(identitySession.sessionToken, assignmentId, routeIds)
        .then(() => startLeader())
        .catch((error) => {
          button.disabled = false;
          button.textContent = 'Take back all unfinished sections';
          window.alert(error instanceof Error ? error.message : 'Could not take back these sections.');
        });
    });
  });
}

function wireLeaderCoverageAnalytics(dashboard: LeaderDashboardResponse, coverage: AdminCoverageDashboard): void {
  const inputId = 'leader-master-coverage-search';
  const sectionInput = document.querySelector<HTMLInputElement>(`#${inputId}`);
  const sectionCount = document.querySelector<HTMLSpanElement>(`#${inputId}-count`);
  sectionInput?.addEventListener('input', () => {
    const query = sectionInput.value.trim().toLocaleLowerCase();
    const shown = query ? coverage.sections.filter((section) => sectionSearchForLeader(section).includes(query)).length : coverage.sections.length;
    if (sectionCount) sectionCount.textContent = `${shown.toLocaleString()} section${shown === 1 ? '' : 's'} shown`;
    leaderCoverageMap?.setSearch(query);
  });

  const scopes: CoverageAnalyticsScope[] = [{ type: 'OVERALL', id: 'OVERALL', label: 'Whole church' }];
  const groups = new Map(dashboard.groups.map((group) => [group.id, group.name]));
  [...groups.entries()]
    .sort((left, right) => left[1].localeCompare(right[1]))
    .forEach(([id, label]) => scopes.push({ type: 'GROUP', id, label }));

  const people = new Map<string, { label: string; aliases: Set<string> }>();
  dashboard.members.forEach((member) => {
    const normalizedName = normalizeText(member.displayName);
    if (!normalizedName) return;
    const person = people.get(normalizedName) ?? { label: member.displayName, aliases: new Set<string>() };
    person.aliases.add(member.id);
    people.set(normalizedName, person);
  });
  [...people.entries()]
    .sort((left, right) => left[1].label.localeCompare(right[1].label))
    .forEach(([normalizedName, person]) => {
      const aliases = [...person.aliases];
      scopes.push({
        type: 'PERSON',
        id: aliases[0] ?? `NAME:${normalizedName}`,
        label: person.label,
        normalizedName,
        ...(aliases.length ? { aliases } : {})
      });
    });
  const scopeInput = document.querySelector<HTMLInputElement>(`#${inputId}-scope`);
  const results = document.querySelector<HTMLDivElement>(`#${inputId}-scope-results`);
  const current = document.querySelector<HTMLDivElement>(`#${inputId}-scope-current`);
  const metrics = document.querySelector<HTMLDivElement>(`#${inputId}-metrics`);
  const clear = document.querySelector<HTMLButtonElement>(`#${inputId}-scope-clear`);
  const choose = (scope: CoverageAnalyticsScope): void => {
    const next = leaderCoverageMap?.setScope(scope) ?? coverageMetricsForScope(coverage.sections, scope);
    if (metrics) metrics.innerHTML = coverageMetricsTemplate(next, scope.type);
    if (current) current.innerHTML = `Showing <strong>${escapeHtml(scope.label)}</strong>`;
    if (scopeInput) scopeInput.value = scope.type === 'OVERALL' ? '' : scope.label;
    results?.classList.add('hidden');
  };
  const renderScopes = (): void => {
    if (!results) return;
    const query = normalizeText(scopeInput?.value ?? '');
    if (!query) {
      results.innerHTML = '';
      results.classList.add('hidden');
      return;
    }
    const matches = scopes
      .filter((scope) => scope.type !== 'OVERALL' && normalizeText(scope.label).includes(query))
      .slice(0, 30);
    results.innerHTML = matches.length ? matches.map((scope) => `<button type="button" data-leader-scope-type="${scope.type}" data-leader-scope-id="${escapeHtml(scope.id)}"><strong>${escapeHtml(scope.label)}</strong><small>${scope.type === 'GROUP' ? 'Small group' : 'Person'}</small></button>`).join('') : '<div class="person-search-state">No matching group or group member.</div>';
    results.classList.remove('hidden');
    results.querySelectorAll<HTMLButtonElement>('[data-leader-scope-id]').forEach((button) => {
      button.addEventListener('click', () => {
        const scope = scopes.find((candidate) => candidate.id === button.dataset.leaderScopeId && candidate.type === button.dataset.leaderScopeType);
        if (scope) choose(scope);
      });
    });
  };
  scopeInput?.addEventListener('input', renderScopes);
  scopeInput?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') results?.classList.add('hidden');
  });
  clear?.addEventListener('click', () => choose(scopes[0]!));
}

function sectionSearchForLeader(section: AdminCoverageSection): string {
  return normalizeText([
    section.id,
    `section ${section.sectionNumber}`,
    section.neighborhoodName,
    ...section.streetNames,
    section.assignedTo?.displayName ?? '',
    section.groupName ?? '',
    ...section.teamMembers.map((member) => member.name)
  ].join(' '));
}

function renderLeaderMemberResults(members: GroupMemberSummary[], query: string): void {
  const results = document.querySelector<HTMLDivElement>('#leader-person-results');
  if (!results) return;
  if (!members.length) {
    results.innerHTML = query ? '<div class="person-search-state">No matching group member.</div>' : '';
    return;
  }
  results.innerHTML = members.slice(0, 30).map((member) => `
    <button type="button" class="person-search-result" data-leader-person-id="${escapeHtml(member.id)}">
      <strong>${escapeHtml(member.displayName)}</strong><small>${member.membershipRole === 'LEADER' ? 'Group leader' : 'Group member'} · Planning Center</small>
    </button>
  `).join('');
}

function showIdentityPin(message = ''): void {
  render(shellTemplate(identityPinTemplate(portal, message), 'live'));
  const form = document.querySelector<HTMLFormElement>('#identity-pin-form');
  const input = document.querySelector<HTMLInputElement>('#identity-last-four');
  const submit = document.querySelector<HTMLButtonElement>('#identity-find-button');

  const updateInput = (next: string): void => {
    if (!input) return;
    input.value = next.replace(/\D/g, '').slice(0, 4);
    input.dispatchEvent(new Event('input'));
  };
  document.querySelectorAll<HTMLButtonElement>('[data-pin-digit]').forEach((button) => {
    button.addEventListener('click', () => updateInput(`${input?.value ?? ''}${button.dataset.pinDigit ?? ''}`));
  });
  document.querySelector<HTMLButtonElement>('[data-pin-action="clear"]')?.addEventListener('click', () => updateInput(''));
  document.querySelector<HTMLButtonElement>('[data-pin-action="backspace"]')?.addEventListener('click', () => updateInput((input?.value ?? '').slice(0, -1)));
  input?.addEventListener('input', () => updateIdentityMessage(''));
  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    const lastFour = input?.value.replace(/\D/g, '') ?? '';
    if (lastFour.length !== 4) {
      updateIdentityMessage('Enter exactly four digits.');
      return;
    }
    if (submit) submit.disabled = true;
    void api.findIdentityCandidates(lastFour, portal)
      .then((result) => {
        showCandidates(result);
      })
      .catch((error) => updateIdentityMessage(error instanceof Error ? error.message : 'Could not search names.'))
      .finally(() => { if (submit) submit.disabled = false; });
  });
  input?.focus();
}

function showCandidates(result: IdentityCandidateResponse): void {
  render(shellTemplate(identityCandidatesTemplate(result, portal), 'live'));
  const radios = Array.from(document.querySelectorAll<HTMLInputElement>('input[name="identity-person"]'));
  let sending = false;
  const sendCode = (personId: string): void => {
    if (sending) return;
    sending = true;
    radios.forEach((radio) => { radio.disabled = true; });
    updateIdentityMessage('Sending a verification code…');
    void api.startIdentityVerification(result.candidateToken, personId)
      .then((verification) => showIdentityVerification(result, personId, verification))
      .catch((error) => {
        radios.forEach((radio) => { radio.disabled = false; });
        const selected = radios.find((radio) => radio.value === personId);
        if (selected) selected.checked = false;
        updateIdentityMessage(error instanceof Error ? error.message : 'Could not send a verification code.');
      })
      .finally(() => { sending = false; });
  };
  radios.forEach((radio) => radio.addEventListener('change', () => {
    if (radio.checked) sendCode(radio.value);
  }));
  document.querySelector<HTMLButtonElement>('#identity-register-self')?.addEventListener('click', () => showSelfRegistrationForm(result));
  document.querySelector<HTMLButtonElement>('#identity-try-again')?.addEventListener('click', () => showIdentityPin());
}

function showSelfRegistrationForm(candidates: IdentityCandidateResponse): void {
  render(shellTemplate(identitySelfRegistrationTemplate(), 'live'));
  const form = document.querySelector<HTMLFormElement>('#identity-registration-form');
  const name = document.querySelector<HTMLInputElement>('#identity-registration-name');
  const phone = document.querySelector<HTMLInputElement>('#identity-registration-phone');
  const submit = document.querySelector<HTMLButtonElement>('#identity-register-button');
  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    if (submit) { submit.disabled = true; submit.textContent = 'Sending code…'; }
    void api.startSelfRegistration(candidates.candidateToken, name?.value ?? '', phone?.value ?? '')
      .then((verification) => showSelfRegistrationVerification(verification))
      .catch((error) => {
        updateIdentityMessage(error instanceof Error ? error.message : 'Could not send a verification code.');
        if (submit) { submit.disabled = false; submit.textContent = 'Text me a verification code'; }
      });
  });
  document.querySelector<HTMLButtonElement>('#identity-registration-back')?.addEventListener('click', () => showCandidates(candidates));
}

function showSelfRegistrationVerification(verification: IdentityVerificationStartResponse): void {
  render(shellTemplate(identityVerificationTemplate(verification), 'live'));
  const form = document.querySelector<HTMLFormElement>('#identity-verification-form');
  const input = document.querySelector<HTMLInputElement>('#identity-verification-code');
  const button = document.querySelector<HTMLButtonElement>('#identity-verify-button');
  input?.addEventListener('input', () => {
    input.value = input.value.replace(/\D/g, '').slice(0, 6);
    updateIdentityMessage('');
  });
  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    const code = input?.value ?? '';
    if (code.length !== 6) { updateIdentityMessage('Enter the six-digit code from your text message.'); return; }
    if (button) button.disabled = true;
    void api.verifyIdentityCode(verification.verificationToken, code)
      .then(completeIdentitySignIn)
      .catch((error) => updateIdentityMessage(error instanceof Error ? error.message : 'Could not verify that code.'))
      .finally(() => { if (button) button.disabled = false; });
  });
  document.querySelector<HTMLButtonElement>('#identity-resend-code')?.remove();
  document.querySelector<HTMLButtonElement>('#identity-change-name')?.addEventListener('click', () => showIdentityPin());
  input?.focus();
}

function showIdentityVerification(
  candidates: IdentityCandidateResponse,
  personId: string,
  verification: IdentityVerificationStartResponse,
  initialMessage = ''
): void {
  render(shellTemplate(identityVerificationTemplate(verification), 'live'));
  if (initialMessage) updateIdentityMessage(initialMessage);
  const form = document.querySelector<HTMLFormElement>('#identity-verification-form');
  const input = document.querySelector<HTMLInputElement>('#identity-verification-code');
  const verifyButton = document.querySelector<HTMLButtonElement>('#identity-verify-button');
  const resendButton = document.querySelector<HTMLButtonElement>('#identity-resend-code');
  input?.addEventListener('input', () => {
    if (input) input.value = input.value.replace(/\D/g, '').slice(0, 6);
    updateIdentityMessage('');
  });
  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    const code = input?.value.replace(/\D/g, '') ?? '';
    if (code.length !== 6) {
      updateIdentityMessage('Enter the six-digit code from your text message.');
      return;
    }
    if (verifyButton) verifyButton.disabled = true;
    void api.verifyIdentityCode(verification.verificationToken, code)
      .then(completeIdentitySignIn)
      .catch((error) => updateIdentityMessage(error instanceof Error ? error.message : 'Could not verify that code.'))
      .finally(() => { if (verifyButton) verifyButton.disabled = false; });
  });
  resendButton?.addEventListener('click', () => {
    if (resendButton.disabled) return;
    resendButton.disabled = true;
    resendButton.textContent = 'Requesting new code…';
    updateIdentityMessage('Requesting a fresh verification text…');
    void api.startIdentityVerification(candidates.candidateToken, personId)
      .then((nextVerification) => showIdentityVerification(
        candidates, personId, nextVerification, 'Code request accepted. Check your phone for a new text.'
      ))
      .catch((error) => updateIdentityMessage(error instanceof Error ? error.message : 'Could not request a new code.'))
      .finally(() => {
        // The old button may have been replaced after a successful request.
        if (!resendButton.isConnected) return;
        resendButton.disabled = false;
        resendButton.textContent = 'Send a new code';
      });
  });
  document.querySelector<HTMLButtonElement>('#identity-change-name')?.addEventListener('click', () => showCandidates(candidates));
  input?.focus();
}

function completeIdentitySignIn(session: IdentitySessionResponse): void {
  identitySession = session;
  saveIdentitySession(session);
  if (portal === 'ADMIN') {
    window.location.assign(adminDestination);
    return;
  }
  if (portal === 'LEADER') {
    void startLeader();
    return;
  }
  // The public entry point is always the volunteer experience. Admin and leader
  // tools remain available when someone explicitly opens /admin or /leader.
  void startUser();
}

function showAssignmentPicker(assignments: AssignmentSummary[]): void {
  if (!identitySession || !bootstrap) return;
  render(shellTemplate(assignmentPickerTemplate(assignments, identitySession.person), bootstrap.mode));
  wireSignOut();
  document.querySelectorAll<HTMLButtonElement>('[data-assignment-token]').forEach((button) => {
    button.addEventListener('click', () => {
      const token = button.dataset.assignmentToken ?? '';
      const selected = assignments.find((item) => item.claimToken === token);
      // Every assignment opened from the volunteer checkout needs a pickup
      // acknowledgment before the map is shown, regardless of its source tag.
      if (selected) openAssignment(selected, true);
    });
  });
}

function openAssignment(nextAssignment: AssignmentSummary, requirePickupConfirmation = true): void {
  assignment = nextAssignment;
  saveClaimToken(nextAssignment.claimToken);
  pickupConfirmed = !requirePickupConfirmation || localStorage.getItem(`${PICKUP_CONFIRMED_PREFIX}${nextAssignment.claimToken}`) === 'true';
  selectedSectionId = nextAssignment.activeSectionId
    ?? nextAssignment.sections.find((section) => section.status !== 'COMPLETE')?.id
    ?? nextAssignment.sections[0]?.id
    ?? '';
  viewMode = 'current';
  showCompletedDashboard = false;
  showAssignment();
}

function showForm(): void {
  if (!bootstrap || !identitySession) return;
  assignment = null;
  showCompletedDashboard = false;
  volunteerSectionPickerMap?.destroy();
  volunteerSectionPickerMap = null;
  render(shellTemplate(formTemplate(bootstrap, state, identitySession.person), bootstrap.mode));
  wireSignOut();

  document.querySelectorAll<HTMLButtonElement>('[data-volunteer-assignment-choice]').forEach((button) => {
    button.addEventListener('click', () => {
      const choice = button.dataset.volunteerAssignmentChoice;
      if (choice !== 'AUTO' && choice !== 'PICK') return;
      if (state.assignmentChoice !== choice) selectedVolunteerSections = [];
      state.assignmentChoice = choice;
      showForm();
    });
  });

  if (!state.assignmentChoice) return;

  const form = document.querySelector<HTMLFormElement>('#assignment-form');
  const neighborhoodSearch = document.querySelector<HTMLInputElement>('#volunteer-neighborhood-search');
  const neighborhoodResults = document.querySelector<HTMLDivElement>('#volunteer-neighborhood-results');
  const customStacks = document.querySelector<HTMLInputElement>('#custom-stacks');
  const stackButtons = document.querySelectorAll<HTMLButtonElement>('[data-stacks]');

  const filterVolunteerNeighborhoods = (): void => {
    const query = (neighborhoodSearch?.value ?? '').trim().toLocaleLowerCase();
    let shown = 0;
    neighborhoodResults?.querySelectorAll<HTMLElement>('[data-volunteer-neighborhood-id]').forEach((option) => {
      const name = option.dataset.volunteerNeighborhoodName?.toLocaleLowerCase() ?? '';
      const visible = Boolean(query) && name.includes(query);
      option.classList.toggle('hidden', !visible);
      if (visible) shown += 1;
    });
    neighborhoodResults?.classList.toggle('hidden', !query || shown === 0);
  };
  neighborhoodSearch?.addEventListener('input', filterVolunteerNeighborhoods);
  neighborhoodResults?.querySelectorAll<HTMLButtonElement>('[data-volunteer-neighborhood-id]').forEach((button) => {
    button.addEventListener('click', () => {
      state.neighborhoodId = button.dataset.volunteerNeighborhoodId ?? '';
      selectedVolunteerSections = [];
      showForm();
    });
  });
  document.querySelector<HTMLButtonElement>('#clear-volunteer-neighborhood')?.addEventListener('click', () => {
    state.neighborhoodId = '';
    selectedVolunteerSections = [];
    showForm();
    window.setTimeout(() => document.querySelector<HTMLInputElement>('#volunteer-neighborhood-search')?.focus(), 0);
  });
  filterVolunteerNeighborhoods();

  if (state.assignmentChoice === 'AUTO') {
    stackButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const stacks = Number(button.dataset.stacks);
        if (!Number.isInteger(stacks)) return;
        state.stacks = stacks;
        if (customStacks) customStacks.value = '';
        updateStackButtons(stackButtons, button);
      });
    });
    customStacks?.addEventListener('input', () => {
      const stacks = Number(customStacks.value);
      if (validStacks(stacks, bootstrap?.maximumStacks ?? 20)) {
        state.stacks = stacks;
        updateStackButtons(stackButtons, null);
      }
    });
  } else {
    document.querySelector<HTMLButtonElement>('#clear-volunteer-sections')?.addEventListener('click', () => {
      volunteerSectionPickerMap?.clear();
    });
    void loadVolunteerSectionPicker();
  }

  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    if (state.assignmentChoice === 'AUTO') {
      const customValue = Number(customStacks?.value);
      if (customStacks?.value && validStacks(customValue, bootstrap?.maximumStacks ?? 20)) state.stacks = customValue;
    }
    void submitAssignment();
  });
}

async function loadVolunteerSectionPicker(): Promise<void> {
  if (!bootstrap || !identitySession || state.assignmentChoice !== 'PICK') return;
  const neighborhoodId = state.neighborhoodId;
  const mapElement = document.querySelector<HTMLDivElement>('#volunteer-section-picker-map');
  if (!mapElement) return;
  if (!neighborhoodId) {
    mapElement.innerHTML = '<div class="map-empty-state"><strong>Choose a neighborhood</strong><p>Start typing above, then select a neighborhood to load its available sections.</p></div>';
    updateVolunteerSectionSummary([]);
    return;
  }
  mapElement.innerHTML = '<div class="map-empty-state"><strong>Loading available sections…</strong><p>The map will show only published sections that are still available.</p></div>';
  updateVolunteerSectionSummary([]);

  try {
    const sections = await api.getVolunteerAvailableSections(identitySession.sessionToken, neighborhoodId);
    if (state.assignmentChoice !== 'PICK' || state.neighborhoodId !== neighborhoodId) return;
    const neighborhoodName = bootstrap.neighborhoods.find((item) => item.id === neighborhoodId)?.name ?? neighborhoodId;
    const dashboard = volunteerPickerDashboard(neighborhoodId, neighborhoodName, sections);
    volunteerSectionPickerMap = mountSectionPickerMap(
      'volunteer-section-picker-map',
      dashboard,
      (selected) => {
        selectedVolunteerSections = selected;
        updateVolunteerSectionSummary(selected);
      },
      bootstrap.maximumStacks
    );
    if (!volunteerSectionPickerMap && sections.length && !sections.some((section) => section.geometry)) {
      mapElement.innerHTML = '<div class="map-empty-state"><strong>Map lines are not available</strong><p>Use “Assign sections for me” for this neighborhood.</p></div>';
    }
  } catch (error) {
    if (error instanceof CoverageApiError && error.statusCode === 401) {
      clearIdentitySession();
      showIdentityPin(error.message);
      return;
    }
    mapElement.innerHTML = `<div class="map-empty-state"><strong>Could not load sections</strong><p>${escapeHtml(error instanceof Error ? error.message : 'Try again.')}</p></div>`;
  }
}

function volunteerPickerDashboard(
  neighborhoodId: string,
  neighborhoodName: string,
  sections: VolunteerAvailableSection[]
): AdminCoverageDashboard {
  const doors = sections.reduce((total, section) => total + section.doors, 0);
  return {
    scope: 'NEIGHBORHOOD',
    neighborhoodId,
    neighborhoodName,
    metrics: {
      totalSections: sections.length,
      totalDoors: doors,
      unassignedSections: sections.length,
      unassignedDoors: doors,
      assignedSections: 0,
      assignedDoors: 0,
      completedSections: 0,
      completedDoors: 0,
      completionPercent: 0
    },
    sections: sections.map((section) => ({
      id: section.id,
      sectionNumber: section.sectionNumber,
      neighborhoodId: section.neighborhoodId,
      neighborhoodName,
      doors: section.doors,
      streetNames: [...section.streetNames],
      status: 'UNASSIGNED',
      routeStatus: 'AVAILABLE',
      teamMembers: [],
      ...(section.geometry ? { geometry: section.geometry } : {}),
      ...(section.center ? { center: section.center } : {})
    })),
    updatedAt: new Date().toISOString()
  };
}

function updateVolunteerSectionSummary(sections: AdminCoverageSection[]): void {
  const summary = document.querySelector<HTMLDivElement>('#volunteer-section-picker-summary');
  const button = document.querySelector<HTMLButtonElement>('#assign-button');
  const count = sections.length;
  const doors = sections.reduce((total, section) => total + section.doors, 0);
  const stackCount = doors > 0 ? Math.ceil(doors / 50) : 0;
  if (summary) {
    summary.innerHTML = count
      ? `<strong>${stackCount.toLocaleString()} tract stack${stackCount === 1 ? '' : 's'} needed</strong><span>${count.toLocaleString()} section${count === 1 ? '' : 's'} selected · ${doors.toLocaleString()} doors</span>`
      : '<strong>0 sections selected</strong><span>Select sections to see how many tract stacks you need.</span>';
  }
  if (button) button.disabled = count === 0;
}

async function submitAssignment(): Promise<void> {
  if (!bootstrap || !identitySession) {
    showIdentityPin('Sign in again to check out sections.');
    return;
  }
  if (!state.assignmentChoice) {
    showFormError('Choose whether the app should assign sections or you want to pick them.');
    return;
  }
  if (!state.neighborhoodId) {
    showFormError('Search for and select a neighborhood.');
    return;
  }
  if (state.assignmentChoice === 'AUTO' && !validStacks(state.stacks, bootstrap.maximumStacks)) {
    showFormError(`Choose between 1 and ${bootstrap.maximumStacks} tract stacks.`);
    return;
  }
  if (state.assignmentChoice === 'PICK' && !selectedVolunteerSections.length) {
    showFormError('Select at least one section on the map.');
    return;
  }
  const message = document.querySelector<HTMLDivElement>('#form-message');
  const button = document.querySelector<HTMLButtonElement>('#assign-button');
  button?.setAttribute('disabled', 'true');
  if (button) button.innerHTML = `<span class="button-spinner" aria-hidden="true"></span><span>${state.assignmentChoice === 'PICK' ? 'Claiming selected sections…' : 'Building your sections…'}</span>`;
  message?.classList.add('hidden');

  try {
    const selectedSectionIds = state.assignmentChoice === 'PICK'
      ? selectedVolunteerSections.map((section) => section.id)
      : [];
    assignment = await api.claimAssignment({
      neighborhoodId: state.neighborhoodId,
      requestedStacks: selectedSectionIds.length
        ? Math.ceil(selectedVolunteerSections.reduce((total, section) => total + section.doors, 0) / 50)
        : state.stacks,
      identitySessionToken: identitySession.sessionToken,
      ...(selectedSectionIds.length ? { selectedSectionIds } : {})
    });
    saveClaimToken(assignment.claimToken);
    // A new checkout always starts at the pickup counter, even if a prior
    // assignment happened to reuse a token or the API omitted its source tag.
    localStorage.removeItem(`${PICKUP_CONFIRMED_PREFIX}${assignment.claimToken}`);
    selectedSectionId = assignment.activeSectionId ?? assignment.sections[0]?.id ?? '';
    viewMode = 'current';
    pickupConfirmed = false;
    showAssignment();
  } catch (error) {
    if (error instanceof CoverageApiError && error.statusCode === 401) {
      clearIdentitySession();
      showIdentityPin(error.message);
      return;
    }
    const messageText = error instanceof Error ? error.message : 'The assignment could not be created.';
    if (state.assignmentChoice === 'PICK') selectedVolunteerSections = [];
    showForm();
    showFormError(messageText);
  }
}

function showAssignment(): void {
  if (!assignment || !bootstrap) return;
  if (!pickupConfirmed && assignment.status !== 'COMPLETE') {
    render(shellTemplate(pickupStacksTemplate(assignment), assignment.developmentPreview ? 'api-preview' : 'live'));
    wireSignOut();
    document.querySelector<HTMLButtonElement>('#stacks-received')?.addEventListener('click', () => {
      pickupConfirmed = true;
      localStorage.setItem(`${PICKUP_CONFIRMED_PREFIX}${assignment!.claimToken}`, 'true');
      showAssignment();
    });
    return;
  }
  if (assignment.status === 'COMPLETE' && !showCompletedDashboard) {
    render(shellTemplate(congratulationsTemplate(assignment), assignment.developmentPreview ? 'api-preview' : 'live'));
    wireSignOut();
    document.querySelector<HTMLButtonElement>('#get-more-maps')?.addEventListener('click', () => {
      void openAssignedMapsOrCheckout();
    });
    document.querySelector<HTMLButtonElement>('#view-completed-master')?.addEventListener('click', () => {
      showCompletedDashboard = true;
      viewMode = 'master';
      showAssignment();
    });
    return;
  }

  if (!selectedSectionId) {
    selectedSectionId = assignment.activeSectionId ?? assignment.sections[0]?.id ?? '';
  }
  render(shellTemplate(
    assignmentDashboardTemplate(assignment, viewMode, selectedSectionId),
    assignment.developmentPreview ? 'api-preview' : 'live'
  ));
  wireSignOut();
  mountVisibleMap();
  wireAssignmentActions();
}

function mountVisibleMap(): void {
  if (!assignment) return;
  const completedIds = assignment.sections
    .filter((section) => section.status === 'COMPLETE')
    .map((section) => section.id);
  if (viewMode === 'master') {
    mountCoverageMap('master-map', assignment.map, {
      completedRouteIds: completedIds,
      ...(assignment.activeSectionId ? { activeRouteId: assignment.activeSectionId } : {}),
      sectionLabels: Object.fromEntries(assignment.sections.map((section) => [section.id, `Section ${section.sequence}`]))
    });
    return;
  }
  const section = currentDisplayedSection();
  if (!section) return;
  mountCoverageMap('section-map', section.map, {
    activeRouteId: section.id,
    completedRouteIds: section.status === 'COMPLETE' ? [section.id] : [],
    sectionLabels: { [section.id]: `Section ${section.sequence}` },
    ...(section.status !== 'COMPLETE' ? {
      onReportDoor: (door: import('./domain/types').DoorPoint, conditionStatus: HouseConditionStatus | null) =>
        reportHouseCondition(section, door.id, conditionStatus)
    } : {})
  });
}

function wireAssignmentActions(): void {
  document.querySelector<HTMLButtonElement>('#current-view-tab')?.addEventListener('click', () => {
    viewMode = 'current';
    showAssignment();
  });
  document.querySelector<HTMLButtonElement>('#master-view-tab')?.addEventListener('click', () => {
    viewMode = 'master';
    showAssignment();
  });
  document.querySelectorAll<HTMLButtonElement>('[data-section-id]').forEach((button) => {
    button.addEventListener('click', () => {
      selectedSectionId = button.dataset.sectionId ?? selectedSectionId;
      viewMode = 'current';
      showAssignment();
    });
  });
  document.querySelector<HTMLButtonElement>('#start-section')?.addEventListener('click', () => {
    const section = currentDisplayedSection();
    if (section) openTeamModal(section, initialTeamForSection(section));
  });
  document.querySelector<HTMLButtonElement>('#complete-section')?.addEventListener('click', () => {
    const section = currentDisplayedSection();
    if (section) void completeSection(section);
  });
  document.querySelector<HTMLButtonElement>('#back-to-assigned-maps')?.addEventListener('click', () => void openAssignedMapsOrCheckout());
  document.querySelector<HTMLButtonElement>('#refresh-assignment')?.addEventListener('click', () => void refreshAssignment());
}

async function reportHouseCondition(
  section: AssignmentSectionSummary,
  houseId: string,
  conditionStatus: HouseConditionStatus | null
): Promise<void> {
  if (!assignment) return;
  try {
    assignment = await api.reportHouseCondition(
      assignment.id,
      section.id,
      houseId,
      assignment.claimToken,
      conditionStatus
    );
    selectedSectionId = section.id;
    viewMode = 'current';
    showAssignment();
  } catch (error) {
    throw error instanceof Error ? error : new Error('The house report could not be saved.');
  }
}

async function refreshAssignment(): Promise<void> {
  if (!assignment) return;
  render(shellTemplate(loadingTemplate('Refreshing your section progress…'), 'loading'));
  try {
    assignment = await api.getAssignmentSession(assignment.claimToken);
    selectedSectionId = assignment.activeSectionId ?? selectedSectionId;
    showAssignment();
  } catch (error) {
    render(shellTemplate(
      `<section class="center-panel error-panel"><h1>Could not refresh</h1><p>${escapeHtml(error instanceof Error ? error.message : 'Try again.')}</p><button id="retry-refresh" class="primary-button" type="button">Try again</button></section>`,
      'loading'
    ));
    document.querySelector<HTMLButtonElement>('#retry-refresh')?.addEventListener('click', () => void refreshAssignment());
  }
}

function openTeamModal(
  section: AssignmentSectionSummary,
  initialMembers: TeamMember[]
): void {
  if (!assignment || !identitySession) return;
  let members = dedupeTeam(initialMembers);
  let searchTimer = 0;
  let searchSequence = 0;

  const draw = (): void => {
    window.clearTimeout(searchTimer);
    removeModal();
    root.insertAdjacentHTML('beforeend', teamModalTemplate(assignment!, section, members));
    const input = document.querySelector<HTMLInputElement>('#team-member-name');
    const results = document.querySelector<HTMLDivElement>('#team-person-results');
    const addCustomButton = document.querySelector<HTMLButtonElement>('#add-custom-team-member');
    const close = (): void => removeModal();
    const updateCustomNameAction = (): void => {
      const hasName = Boolean(input?.value.trim());
      addCustomButton?.classList.toggle('hidden', !hasName);
    };
    const setMessage = (text: string): void => {
      const message = document.querySelector<HTMLDivElement>('#team-modal-message');
      if (!message) return;
      message.textContent = text;
      message.classList.toggle('hidden', !text);
    };
    const addPlanningCenterPerson = (person: PersonSearchResult): void => {
      const personId = person.pcoPersonId ?? person.id;
      members = dedupeTeam([...members, {
        name: person.displayName,
        source: 'PLANNING_CENTER',
        personId
      }]);
      draw();
    };
    const renderResults = (people: PersonSearchResult[], message = ''): void => {
      if (!results) return;
      if (message) {
        results.innerHTML = `<div class="person-search-state">${escapeHtml(message)}</div>`;
        return;
      }
      results.innerHTML = people.map((person) => `
        <button type="button" class="person-search-result" data-team-person-id="${escapeHtml(person.id)}">
          <strong>${escapeHtml(person.displayName)}</strong>
        </button>
      `).join('');
      document.querySelectorAll<HTMLButtonElement>('[data-team-person-id]').forEach((button) => {
        button.addEventListener('click', () => {
          const person = people.find((candidate) => candidate.id === button.dataset.teamPersonId);
          if (person) addPlanningCenterPerson(person);
        });
      });
    };
    const searchPeople = (): void => {
      const query = input?.value.trim() ?? '';
      const requestSequence = ++searchSequence;
      if (query.length < 2) {
        renderResults([], query ? 'Type at least two letters.' : '');
        return;
      }
      renderResults([], 'Searching names…');
      void api.searchPlanningCenterPeople(identitySession!.sessionToken, query)
        .then((people) => {
          if (requestSequence !== searchSequence) return;
          renderResults(people, people.length ? '' : 'No matching name.');
        })
        .catch((error) => {
          if (requestSequence !== searchSequence) return;
          renderResults([], error instanceof Error ? error.message : 'Could not search names.');
        });
    };

    document.querySelector<HTMLButtonElement>('#close-team-modal')?.addEventListener('click', close);
    document.querySelector<HTMLButtonElement>('#cancel-team-modal')?.addEventListener('click', close);
    document.querySelectorAll<HTMLButtonElement>('[data-remove-team-index]').forEach((button) => {
      button.addEventListener('click', () => {
        const index = Number(button.dataset.removeTeamIndex);
        if (!Number.isInteger(index)) return;
        members = members.filter((_, candidateIndex) => candidateIndex !== index);
        draw();
      });
    });
    input?.addEventListener('input', () => {
      setMessage('');
      updateCustomNameAction();
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(searchPeople, 250);
    });
    addCustomButton?.addEventListener('click', () => {
      const name = input?.value.trim() ?? '';
      if (!name) {
        setMessage('Type the person’s full name first.');
        return;
      }
      members = dedupeTeam([...members, { name, source: 'CUSTOM' }]);
      draw();
    });
    document.querySelector<HTMLButtonElement>('#confirm-team')?.addEventListener('click', () => {
      if (!members.length) {
        setMessage('Add at least one person doing this section.');
        return;
      }
      void startSection(section, members);
    });
    updateCustomNameAction();
    window.setTimeout(() => input?.focus(), 0);
  };

  draw();
}

async function startSection(section: AssignmentSectionSummary, teamMembers: TeamMember[]): Promise<void> {
  if (!assignment) return;
  const button = document.querySelector<HTMLButtonElement>('#confirm-team');
  button?.setAttribute('disabled', 'true');
  if (button) button.textContent = 'Starting…';
  try {
    assignment = await api.startAssignmentSection(
      assignment.id,
      section.id,
      assignment.claimToken,
      teamMembers
    );
    selectedSectionId = section.id;
    viewMode = 'current';
    removeModal();
    showAssignment();
  } catch (error) {
    const message = document.querySelector<HTMLDivElement>('#team-modal-message');
    if (message) {
      message.textContent = error instanceof Error ? error.message : 'The section could not be started.';
      message.classList.remove('hidden');
    }
    button?.removeAttribute('disabled');
    if (button) button.textContent = 'Start section';
  }
}


async function completeSection(section: AssignmentSectionSummary): Promise<void> {
  if (!assignment) return;
  const button = document.querySelector<HTMLButtonElement>('#complete-section');
  button?.setAttribute('disabled', 'true');
  if (button) button.textContent = 'Completing…';
  const sectionTeam = section.teamMembers.length ? section.teamMembers : initialTeamForSection(section);
  const completionComment = document.querySelector<HTMLTextAreaElement>('#section-completion-comment')?.value.trim() ?? '';
  try {
    assignment = await api.completeAssignmentSection(
      assignment.id,
      section.id,
      assignment.claimToken,
      sectionTeam,
      completionComment || undefined
    );
    if (assignment.status === 'COMPLETE') {
      showCompletedDashboard = false;
      await routeAfterCompletedAssignment();
      return;
    }
    const next = assignment.sections.find((candidate) => candidate.id === assignment?.activeSectionId)
      ?? assignment.sections.find((candidate) => candidate.status !== 'COMPLETE');
    selectedSectionId = next?.id ?? '';
    viewMode = 'current';
    showAssignment();
  } catch (error) {
    button?.removeAttribute('disabled');
    if (button) button.textContent = 'Complete this section';
    window.alert(error instanceof Error ? error.message : 'The section could not be completed.');
  }
}

async function openAssignedMapsOrCheckout(): Promise<void> {
  if (!identitySession || !bootstrap) {
    showIdentityPin('Sign in again to continue.');
    return;
  }

  clearClaimToken();
  render(shellTemplate(loadingTemplate('Checking your assigned maps…'), 'loading'));
  try {
    const activeAssignments = await api.getMyAssignments(identitySession.sessionToken);
    if (activeAssignments.length) {
      assignment = null;
      selectedSectionId = '';
      viewMode = 'current';
      showAssignmentPicker(activeAssignments);
      return;
    }
    state.stacks = bootstrap.defaultStacks;
    showForm();
  } catch (error) {
    showOpeningError(error, () => void openAssignedMapsOrCheckout());
  }
}

async function routeAfterCompletedAssignment(): Promise<void> {
  if (!assignment || !identitySession || !bootstrap) {
    showAssignment();
    return;
  }

  clearClaimToken();
  render(shellTemplate(loadingTemplate('Loading your assigned maps…'), 'loading'));
  try {
    const activeAssignments = await api.getMyAssignments(identitySession.sessionToken);
    if (activeAssignments.length) {
      assignment = null;
      selectedSectionId = '';
      viewMode = 'current';
      showAssignmentPicker(activeAssignments);
      return;
    }
  } catch {
    // The completed assignment is still safe to show if the assignment-list refresh fails.
  }

  showAssignment();
}

function currentDisplayedSection(): AssignmentSectionSummary | undefined {
  if (!assignment) return undefined;
  return assignment.sections.find((section) => section.id === selectedSectionId)
    ?? assignment.sections.find((section) => section.id === assignment?.activeSectionId)
    ?? assignment.sections.find((section) => section.status !== 'COMPLETE')
    ?? assignment.sections[0];
}

function initialTeamForSection(section: AssignmentSectionSummary): TeamMember[] {
  if (!assignment) return [];
  if (section.teamMembers.length) return section.teamMembers.map((member) => ({ ...member }));
  const previous = assignment.sections
    .filter((candidate) => candidate.sequence < section.sequence && candidate.teamMembers.length)
    .sort((left, right) => right.sequence - left.sequence)[0];
  if (previous) return previous.teamMembers.map((member) => ({ ...member }));
  return [{
    name: assignment.volunteer.name,
    source: assignment.volunteer.personId ? 'PLANNING_CENTER' : 'CUSTOM',
    ...(assignment.volunteer.personId ? { personId: assignment.volunteer.personId } : {})
  }];
}

function dedupeTeam(members: TeamMember[]): TeamMember[] {
  const byKey = new Map<string, TeamMember>();
  members.forEach((member) => {
    const name = member.name.trim();
    if (!name) return;
    const key = member.personId ? `PCO:${member.personId}` : `NAME:${name.toLocaleLowerCase()}`;
    byKey.set(key, { ...member, name });
  });
  return Array.from(byKey.values());
}

function removeModal(): void {
  document.querySelector('#team-modal-backdrop')?.remove();
}

function updateStackButtons(buttons: NodeListOf<HTMLButtonElement>, active: HTMLButtonElement | null): void {
  buttons.forEach((candidate) => {
    const isActive = candidate === active;
    candidate.classList.toggle('active', isActive);
    candidate.setAttribute('aria-pressed', String(isActive));
  });
}

function validStacks(value: number, maximum: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= maximum;
}

function showFormError(message: string): void {
  const element = document.querySelector<HTMLDivElement>('#form-message');
  if (!element) return;
  element.textContent = message;
  element.classList.remove('hidden');
}

function wireSignOut(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-identity-sign-out], #identity-sign-out').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      signOutIdentity();
    });
  });
}

function signOutIdentity(): void {
  clearIdentitySession();
  clearClaimToken();
  reloadLatestApp('/');
}

function showAccessDenied(area: 'Leader' | 'Admin', detail = ''): void {
  const personName = identitySession?.person.displayName ?? 'This account';
  const allowedLinks = identitySession
    ? [
        identitySession.person.roles.includes('ADMIN') ? '<a class=\"primary-button\" href=\"/admin\">Admin</a>' : '',
        identitySession.person.roles.includes('LEADER') ? '<a class=\"primary-button\" href=\"/leader\">Leader</a>' : '',
        '<a class=\"secondary-button\" href=\"/\">Volunteer</a>'
      ].filter(Boolean).join('')
    : '<a class=\"primary-button\" href=\"/\">Sign in</a>';
  const message = detail || `${personName} does not currently have ${area.toLowerCase()} access.`;
  render(shellTemplate(
    `<section class=\"center-panel error-panel\"><h1>${area} access isn’t enabled</h1><p>${escapeHtml(message)}</p><p>Your sign-in is still active. Choose another area below.</p><div class=\"access-denied-actions\">${allowedLinks}</div></section>`,
    'live'
  ));
  wireSignOut();
}

function showOpeningError(error: unknown, retry: () => void): void {
  const message = error instanceof Error ? error.message : 'The app could not load.';
  render(shellTemplate(`<section class="center-panel error-panel"><h1>Could not open the app</h1><p>${escapeHtml(message)}</p><button id="retry-button" class="primary-button" type="button">Try again</button></section>`, 'loading'));
  document.querySelector<HTMLButtonElement>('#retry-button')?.addEventListener('click', retry);
}

function updateIdentityMessage(message: string): void {
  const element = document.querySelector<HTMLDivElement>('#identity-message');
  if (!element) return;
  element.textContent = message;
  element.classList.toggle('hidden', !message);
}

function loadIdentitySession(): StoredIdentitySession | null {
  try {
    const raw = window.localStorage.getItem(IDENTITY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredIdentitySession>;
    if (
      typeof parsed.sessionToken !== 'string' ||
      typeof parsed.expiresAt !== 'string' ||
      !parsed.person ||
      typeof parsed.person.id !== 'string' ||
      Date.now() >= Date.parse(parsed.expiresAt)
    ) {
      window.localStorage.removeItem(IDENTITY_STORAGE_KEY);
      return null;
    }
    const role = parsed.person.role === 'ADMIN' || parsed.person.role === 'LEADER' ? parsed.person.role : 'USER';
    parsed.person.roles = Array.isArray(parsed.person.roles) && parsed.person.roles.length
      ? parsed.person.roles
      : role === 'ADMIN' ? ['USER', 'ADMIN'] : role === 'LEADER' ? ['USER', 'LEADER'] : ['USER'];
    return parsed as StoredIdentitySession;
  } catch {
    return null;
  }
}

function saveIdentitySession(session: StoredIdentitySession): void {
  try { window.localStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(session)); } catch { /* ignore */ }
}

function clearIdentitySession(): void {
  identitySession = null;
  try { window.localStorage.removeItem(IDENTITY_STORAGE_KEY); } catch { /* ignore */ }
}

function loadClaimToken(): string {
  try {
    return window.localStorage.getItem(CLAIM_TOKEN_STORAGE_KEY)?.trim() ?? '';
  } catch {
    return '';
  }
}

function saveClaimToken(token: string): void {
  try {
    window.localStorage.setItem(CLAIM_TOKEN_STORAGE_KEY, token);
  } catch {
    // The assignment still works in the current page when storage is unavailable.
  }
}

function clearClaimToken(): void {
  try {
    window.localStorage.removeItem(CLAIM_TOKEN_STORAGE_KEY);
  } catch {
    // Ignore storage restrictions.
  }
}

function normalizeText(value: string): string {
  return value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
}

function render(markup: string): void {
  root.innerHTML = markup;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => {
    const replacements: Record<string, string> = {
      '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
    };
    return replacements[character] ?? character;
  });
}

import { installAdminDashboardLayout } from './admin/dashboard-layout';

installAdminDashboardLayout();
